import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useState } from 'react';
import { Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { ApiError, api } from '../api/client';
import type { Action, Row } from '../api/types';
import { useAuth, useCan } from '../auth/AuthContext';
import { invalidateData } from '../crud/CrudScreen';
import { CheckField, FormError, MobileField, SelectField, TextField, applyServerErrors } from '../forms/fields';
import { emailReq, mobileReq, newPasswordIssue, optStr, req, toText } from '../forms/zod';
import { DataTable, type Column } from '../ui/DataTable';
import { useConfirm, useToast } from '../ui/feedback';
import { fmtDateTime } from '../ui/format';
import { PageHeader } from '../ui/PageHeader';
import { useListQuery } from '../ui/useListQuery';

const ACTIONS: Action[] = ['view', 'create', 'edit', 'delete'];
type Perms = Record<string, Record<Action, boolean>>;
interface MenuRow { module: string; code: string; label: string }

const schema = z.object({
  name: req(),
  email: emailReq,
  mobileNo: mobileReq, // required by the legacy screen
  employeeCode: optStr,
  roleId: z.string().min(1, 'Select a role'),
  isActive: z.boolean(),
  password: z.string().optional(),
  confirmPassword: z.string().optional(),
  permissions: z.record(z.string(), z.record(z.string(), z.boolean())),
});
type Values = z.infer<typeof schema>;

const emptyPerms = (menus: MenuRow[]): Perms => Object.fromEntries(menus.map((m) => [m.code, { view: false, create: false, edit: false, delete: false }]));

/**
 * Rights matrix: one row per menu, one column per action. You can only grant what you hold yourself.
 * Each module section can be collapsed (handy once there are many), and both a module and the whole
 * matrix have one-click "All / None" actions, so setting up e.g. a full-access Admin isn't dozens of
 * individual clicks.
 */
function PermissionMatrix({ form, menus, disabled }: { form: UseFormReturn<Values>; menus: MenuRow[]; disabled: boolean }) {
  const { can } = useAuth();
  const modules = [...new Set(menus.map((m) => m.module))];
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const setMany = (codes: string[], on: boolean) => {
    for (const code of codes) for (const a of ACTIONS) if (can(code, a)) form.setValue(`permissions.${code}.${a}`, on, { shouldDirty: true });
  };
  const allCodes = menus.map((m) => m.code);
  return (
    <div>
      <div className="d-flex justify-content-end gap-3 mb-2">
        <Button size="sm" variant="link" className="p-0" disabled={disabled} onClick={() => setMany(allCodes, true)}>Grant all</Button>
        <Button size="sm" variant="link" className="p-0" disabled={disabled} onClick={() => setMany(allCodes, false)}>Clear all</Button>
      </div>
      <div className="table-responsive border rounded">
        <Table size="sm" className="mb-0 align-middle">
          <thead>
            <tr><th>Menu</th>{ACTIONS.map((a) => <th key={a} className="text-center text-capitalize">{a}</th>)}<th /></tr>
          </thead>
          <tbody>
            {modules.map((mod) => (
              <ModuleRows
                key={mod} module={mod} rows={menus.filter((m) => m.module === mod)} form={form} disabled={disabled} can={can}
                collapsed={!!collapsed[mod]} onToggle={() => setCollapsed((s) => ({ ...s, [mod]: !s[mod] }))} setMany={setMany}
              />
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

function ModuleRows({ module, rows, form, disabled, can, collapsed, onToggle, setMany }: {
  module: string; rows: MenuRow[]; form: UseFormReturn<Values>; disabled: boolean; can: (m: string, a?: Action) => boolean;
  collapsed: boolean; onToggle: () => void; setMany: (codes: string[], on: boolean) => void;
}) {
  const codes = rows.map((r) => r.code);
  return (
    <Fragment>
      <tr className="mms-subhead">
        <th colSpan={5} className="small text-uppercase text-body-secondary">
          <button type="button" className="btn btn-link btn-sm p-0 text-reset text-decoration-none fw-semibold text-uppercase" onClick={onToggle} aria-expanded={!collapsed}>
            <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-down'} me-1`} aria-hidden />{module}
          </button>
        </th>
        <th className="text-end text-nowrap">
          <Button size="sm" variant="link" className="p-0 me-2" disabled={disabled} onClick={() => setMany(codes, true)}>All</Button>
          <Button size="sm" variant="link" className="p-0" disabled={disabled} onClick={() => setMany(codes, false)}>None</Button>
        </th>
      </tr>
      {!collapsed && rows.map((m) => (
        <tr key={m.code}>
          <td>{m.label}</td>
          {ACTIONS.map((a) => (
            <td key={a} className="text-center">
              <Form.Check
                type="checkbox"
                aria-label={`${m.label} ${a}`}
                disabled={disabled || !can(m.code, a)}
                title={can(m.code, a) ? undefined : 'You cannot grant a right you do not hold'}
                {...form.register(`permissions.${m.code}.${a}`, {
                  onChange: (e) => {
                    // any right implies view; removing view removes the rest
                    if (e.target.checked && a !== 'view') form.setValue(`permissions.${m.code}.view`, true, { shouldDirty: true });
                    if (!e.target.checked && a === 'view') for (const x of ACTIONS) form.setValue(`permissions.${m.code}.${x}`, false, { shouldDirty: true });
                  },
                })}
              />
            </td>
          ))}
          <td className="text-end text-nowrap">
            <Button size="sm" variant="link" className="p-0 me-2" disabled={disabled} onClick={() => setMany([m.code], true)}>All</Button>
            <Button size="sm" variant="link" className="p-0" disabled={disabled} onClick={() => setMany([m.code], false)}>None</Button>
          </td>
        </tr>
      ))}
    </Fragment>
  );
}

function UserModal({ userId, onClose }: { userId: number | null; onClose: () => void }) {
  const { me } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const isNew = userId === null;
  const isSelf = userId !== null && userId === me?.user.id;
  const canSave = useCan('users', isNew ? 'create' : 'edit');
  const [serverError, setServerError] = useState<string | null>(null);

  const menusQ = useQuery({ queryKey: ['users', 'menus'], queryFn: () => api.get<MenuRow[]>('/users/menus'), staleTime: 300_000 });
  const userQ = useQuery({ queryKey: ['users', 'one', userId], queryFn: () => api.get<Row>(`/users/${userId}`), enabled: !isNew });
  const menus = menusQ.data ?? [];

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', mobileNo: '', employeeCode: '', roleId: '2', isActive: true, password: '', confirmPassword: '', permissions: {} },
  });

  useEffect(() => {
    if (!menusQ.data) return;
    const perms = emptyPerms(menusQ.data);
    if (userQ.data) {
      for (const p of userQ.data.permissions as Row[]) perms[p.menuCode] = { view: p.view, create: p.create, edit: p.edit, delete: p.delete };
      const u = userQ.data;
      form.reset({ name: u.name, email: u.email, mobileNo: u.mobileNo ?? '', employeeCode: u.employeeCode ?? '', roleId: String(u.roleId), isActive: !!u.isActive, password: '', confirmPassword: '', permissions: perms });
    } else if (isNew) {
      form.reset({ ...form.getValues(), permissions: perms });
    }
  }, [menusQ.data, userQ.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v: Values) => {
      const permissions = Object.entries(v.permissions)
        .filter(([, r]) => ACTIONS.some((a) => r[a]))
        .map(([menuCode, r]) => ({ menuCode, view: !!r.view, create: !!r.create, edit: !!r.edit, delete: !!r.delete }));
      const body: Record<string, unknown> = { name: v.name.trim(), email: v.email.trim(), mobileNo: toText(v.mobileNo), employeeCode: toText(v.employeeCode) };
      if (v.password) body.password = v.password;
      if (isNew || !isSelf) Object.assign(body, { roleId: Number(v.roleId), isActive: v.isActive, permissions });
      return isNew ? api.post('/users', body) : api.patch(`/users/${userId}`, body);
    },
    onSuccess: async () => {
      await invalidateData(qc);
      toast.success(isNew ? 'User created' : 'User updated');
      onClose();
    },
    onError: (e) => setServerError(applyServerErrors(form, e)),
  });

  const submit = form.handleSubmit((v) => {
    const problem = newPasswordIssue(v.password, isNew);
    if (problem) {
      form.setError('password', { message: problem });
      return;
    }
    if ((v.password ?? '') !== (v.confirmPassword ?? '')) {
      form.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    // Legacy rule ("Select Menus"): a user must be able to open at least one menu. Your own account is exempt.
    if (!isSelf && !Object.values(v.permissions).some((r) => ACTIONS.some((a) => r[a]))) {
      setServerError('Select at least one menu for this user');
      return;
    }
    setServerError(null);
    save.mutate(v);
  });

  const loading = menusQ.isPending || (!isNew && userQ.isPending);
  const readOnly = !canSave;

  return (
    <Modal show onHide={onClose} size="xl" fullscreen="sm-down" backdrop="static" centered scrollable>
      <Form noValidate className="mms-modal-form" onSubmit={submit}>
        <Modal.Header closeButton><Modal.Title as="h2" className="h5">{isNew ? 'New user' : `Edit user`}</Modal.Title></Modal.Header>
        <Modal.Body>
          {loading ? <div className="text-center py-5"><Spinner animation="border" role="status" /></div> : (
            <>
              <FormError message={serverError} />
              {isSelf && <div className="alert alert-info py-2">This is your own account: you can change your details and password, but not your role, status or permissions.</div>}
              <div className="row">
                <div className="col-md-6"><TextField form={form as never} name="name" label="Full name" required disabled={readOnly} /></div>
                <div className="col-md-6"><TextField form={form as never} name="email" label="Email (login)" type="email" required disabled={readOnly} /></div>
                <div className="col-md-4"><MobileField form={form as never} name="mobileNo" label="Mobile" required disabled={readOnly} /></div>
                <div className="col-md-4"><TextField form={form as never} name="employeeCode" label="Employee code" disabled={readOnly} /></div>
                <div className="col-md-4"><SelectField form={form as never} name="roleId" label="Role" required disabled={readOnly || isSelf} options={[{ value: '1', label: 'Admin' }, { value: '2', label: 'User' }]} /></div>
                <div className="col-md-6">
                  <TextField form={form as never} name="password" label={isNew ? 'Password' : 'New password'} type="password" autoComplete="new-password" required={isNew} disabled={readOnly}
                    help={isNew ? '8+ characters with a letter and a digit' : 'Leave empty to keep the current password. Setting one signs the user out everywhere.'} />
                </div>
                <div className="col-md-6">
                  <TextField form={form as never} name="confirmPassword" label="Confirm password" type="password" autoComplete="new-password" required={isNew} disabled={readOnly} />
                </div>
                <div className="col-md-6 d-flex align-items-center"><CheckField form={form as never} name="isActive" label="Active (can sign in)" disabled={readOnly || isSelf} /></div>
              </div>
              <h3 className="h6 mt-2">Permissions</h3>
              <PermissionMatrix form={form} menus={menus} disabled={readOnly || isSelf} />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
          {!readOnly && <Button type="submit" disabled={save.isPending || loading}>{save.isPending ? 'Saving…' : 'Save'}</Button>}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export function UsersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { me } = useAuth();
  const canCreate = useCan('users', 'create');
  const canEdit = useCan('users', 'edit');
  const canDelete = useCan('users', 'delete');
  const { query, state } = useListQuery('/users', { initialSort: { key: 'name', dir: 'asc' } });
  const [modal, setModal] = useState<{ id: number | null } | null>(null);

  const del = useMutation({
    mutationFn: (row: Row) => api.delete(`/users/${row.id}`),
    onSuccess: async () => { await invalidateData(qc); toast.success('User deleted'); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Delete failed'),
  });

  const columns: Column[] = [
    { key: 'name', header: 'Name', sortKey: 'name', className: 'fw-semibold' },
    { key: 'email', header: 'Email', sortKey: 'email' },
    { key: 'mobileNo', header: 'Mobile', hideOnMobile: true },
    { key: 'role', header: 'Role', sortKey: 'role' },
    { key: 'menuCount', header: 'Menus', align: 'end', hideOnMobile: true, render: (r) => <span title="Menus this user can open">{r.menuCount}</span> },
    { key: 'isActive', header: 'Status', render: (r) => (r.isActive ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>) },
    { key: 'lastLoginAt', header: 'Last sign-in', className: 'text-nowrap', hideOnMobile: true, render: (r) => (r.lastLoginAt ? fmtDateTime(r.lastLoginAt) : <span className="text-body-tertiary">never</span>) },
  ];

  return (
    <>
      <PageHeader title="Manage Users" icon="people" actions={canCreate && <Button onClick={() => setModal({ id: null })}><i className="bi bi-plus-lg me-1" aria-hidden />New user</Button>} />
      <div className="card mb-3"><div className="card-body py-2">
        <Form.Control size="sm" type="search" placeholder="Search name, email, mobile…" aria-label="Search" value={state.q} onChange={(e) => state.setQ(e.target.value)} style={{ maxWidth: 300 }} />
      </div></div>
      <div className="card"><div className="card-body p-2 p-md-3">
        <DataTable
          columns={columns} rows={query.data?.data} loading={query.isPending} fetching={query.isFetching}
          sort={state.sort} dir={state.dir} onSort={state.toggleSort}
          page={state.page} pageSize={state.pageSize} total={query.data?.total} onPage={state.setPage} caption="Users"
          actions={(row) => (
            <>
              <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setModal({ id: row.id })} aria-label={`${canEdit ? 'Edit' : 'View'} ${row.name}`}>
                <i className={`bi ${canEdit ? 'bi-pencil' : 'bi-eye'}`} aria-hidden />
              </Button>
              {canDelete && row.id !== me?.user.id && (
                <Button size="sm" variant="outline-danger" aria-label={`Delete ${row.name}`} disabled={del.isPending}
                  onClick={async () => { if (await confirm({ title: 'Delete user', message: <>Delete <strong>{row.name}</strong> ({row.email})? They will be signed out and can no longer log in.</>, confirmLabel: 'Delete' })) del.mutate(row); }}>
                  <i className="bi bi-trash" aria-hidden />
                </Button>
              )}
            </>
          )}
        />
      </div></div>
      {modal && <UserModal userId={modal.id} onClose={() => setModal(null)} />}
    </>
  );
}
